import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router';
import l10n from '@built/i18n';

import * as built from '../built';
import SetupPassword from '../components/SetupPassword';
import { signUpUser, setInitialPassword } from '../actions';
import {
  AuthError,
  BrandingData,
  StoreState,
  SetupState,
  AuthData
} from '../types';

import {
  getAuthData,
  getAuthError,
  getBranding,
  getNewPasswordRequired,
  getUsername,
  getRegistrationToken
} from '../reducers';

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(
    class SetupPasswordContainer extends React.Component<
      SetupPasswordContainerProps,
      SetupPasswordContainerState
    > {
      constructor(props: SetupPasswordContainerProps) {
        super(props);
        this.state = {
          password: '',
          preferredLocale: l10n.language
        };
      }

      render() {
        return (
          <SetupPassword
            {...this.state}
            username={this.props.username}
            authError={this.props.authError}
            needsPassword={this.props.needsPassword}
            branding={this.props.branding}
            registering={this.props.registrationToken ? true : false}
            preferredLocale={this.state.preferredLocale}
            isImaUser={this.props.isImaUser}
            onChange={this.handleChange}
            onChangeLocale={this.handleLocaleChange}
            onSubmit={this.handleSubmit}
          />
        );
      }

      handleChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
        const password = event.currentTarget.value;
        this.setState({ password });
      };

      handleLocaleChange = (event: React.SyntheticEvent<HTMLSelectElement>) => {
        const preferredLocale = event.currentTarget.value;
        this.setState({ preferredLocale });
        location.search = location.search.replace(
          /lng=[^&$]*/i,
          `lng=${preferredLocale}`
        );
      };

      handleSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
        const { state, props } = this;
        const { password, preferredLocale } = state;
        const {
          registrationToken,
          setInitialPassword: innerSetInitialPassword,
          signUpUser: innerSignUpUser,
          username,
          updateState
        } = props;

        event.preventDefault();
        this.setState({ busy: true });

        if (registrationToken) {
          // Adjust language if it was changed
          built
            .changeLocale(registrationToken, username, preferredLocale)
            .then(resp => {
              l10n.updateUserLanguage(preferredLocale);
            });

          // if the user needs a new password and we have a registration token,
          // then we know the user is signing up for the first time and should
          // sign them up in cognito
          innerSignUpUser(username, password, registrationToken).then(() => {
            updateState(SetupState.completedPassword);
          });
        } else {
          // we don't have a registration token, so we're completing a user migration
          // and need to respond to the new password challenge cognito requires
          innerSetInitialPassword(password).then(() => {
            updateState(SetupState.completedPassword);
          });
        }
      };
    }
  )
);

export interface SetupPasswordContainerProps extends RouteComponentProps<{}> {
  /** Current state of auth data */
  authData: AuthData;

  /** An error to display */
  authError?: AuthError;

  /** Whether the user needs to setup a new password */
  needsPassword?: boolean;

  /** Branding data to customize the UI */
  branding?: BrandingData;

  /** Is this an IMA user? */
  isImaUser?: boolean;

  /** The username of the current user */
  username: string;
  registrationToken: string;
  preferredLocale: string;
  updateState(newState: SetupState): void;
  signUpUser(
    username: string,
    password: string,
    registrationToken: string
  ): Promise<boolean>;
  setInitialPassword(password: string): Promise<boolean>;
}

export interface SetupPasswordContainerState {
  /** The currently entered password value */
  password: string;
  /** The currently selected preferredLocale value */
  preferredLocale: string;
  /** Whether to indicate that the app is currently processing */
  busy?: boolean;
  error?: string;
}

function mapStateToProps(
  state: StoreState,
  _ownProps?: Pick<SetupPasswordContainerProps, 'updateState'> &
    RouteComponentProps<{}>
): Pick<
  SetupPasswordContainerProps,
  | 'authData'
  | 'authError'
  | 'needsPassword'
  | 'username'
  | 'branding'
  | 'registrationToken'
  | 'isImaUser'
> {
  const branding = getBranding(state);
  return {
    authData: getAuthData(state),
    authError: getAuthError(state),
    username: getUsername(state)!,
    registrationToken: getRegistrationToken(state)!,
    needsPassword: getNewPasswordRequired(state),
    isImaUser: getAuthData(state).isImaUser,
    branding
  };
}

function mapDispatchToProps(
  dispatch: Dispatch<StoreState>
): Pick<SetupPasswordContainerProps, 'signUpUser' | 'setInitialPassword'> {
  return {
    signUpUser(username: string, password: string, registrationToken: string) {
      return dispatch(signUpUser(username, password, registrationToken));
    },
    setInitialPassword(password: string) {
      return dispatch(setInitialPassword(password));
    }
  };
}



// WEBPACK FOOTER //
// ./src/containers/SetupPassword.tsx